import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChatThread, ChatComposer, markRated } from "@/components/Chat";
import { AgentAvatar } from "@/components/AgentAvatar";
import AddSettingDialog from "@/components/AddSettingDialog";
import TeamTestsNotice from "@/components/laser/TeamTestsNotice";
import { Ion } from "@/components/Ion";
import { useBack } from "@/components/Orig";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";

const LASER_MODELS = ["300W", "500W", "1000W", "2000W"];
const EXAMPLES = ["What settings for rust on cast iron?", "My chiller temp keeps rising — what should I check?", "Best line spacing for paint removal on aluminum", "What PPE do I need for cleaning galvanized steel?"];
const DEFAULT_STATS = { avg_seconds: 12, p90_seconds: 22, is_estimate: true };

const LaserPicker = ({ theme, owned, active, onSave, onClose, machines = [], machineId, onMachine }) => {
  const btn = (on) => ({ borderColor: on ? theme.accent : theme.border, background: theme.surface });
  return (
    <div className="m-4 rounded-[14px] border p-4" style={{ background: theme.card, borderColor: theme.border }} data-testid="laser-picker">
      {machines.length > 0 && <>
        <p className="mb-1 text-sm font-bold">Your registered machine</p>
        <p className="mb-2 text-[11px]" style={{ color: theme.textSecondary }}>The agent uses test pieces from this exact model first.</p>
        <div className="mb-4 flex flex-wrap gap-2">{machines.map((m) => <button key={m.id} data-testid={`agent-machine-${m.id}`} onClick={() => onMachine(m)} className="rounded-lg border px-3 py-2.5 text-xs font-bold" style={{ ...btn(machineId === m.id), color: machineId === m.id ? theme.accent : theme.text }}>{m.nickname || m.name}</button>)}</div>
      </>}
      <p className="mb-1 text-sm font-bold">Which lasers do you own?</p>
      <p className="mb-2 text-[11px]" style={{ color: theme.textSecondary }}>Optional — the AI can also ask you in chat. Tap to add or remove.</p>
      <div className="flex flex-wrap gap-2">
        {LASER_MODELS.map((m) => {
          const on = owned.includes(m);
          return (
            <button key={m} data-testid={`laser-own-${m}`} onClick={() => { const next = on ? owned.filter((x) => x !== m) : [...owned, m]; onSave({ owned_lasers: next, laser_model: on && active === m ? next[0] || null : active || next[0] || null }); }}
              className="flex items-center gap-1.5 rounded-lg border px-3 py-2.5" style={btn(on)}>
              <Ion name={on ? "checkmark-circle" : "ellipse-outline"} size={14} color={on ? theme.accent : theme.textSecondary} />
              <span className="text-xs font-bold" style={{ color: on ? theme.accent : theme.text }}>{m}</span>
            </button>
          );
        })}
      </div>
      {owned.length > 1 && <>
        <p className="mb-1 mt-4 text-sm font-bold">Active laser for this chat</p>
        <div className="flex flex-wrap gap-2">{owned.map((m) => <button key={m} data-testid={`laser-active-${m}`} onClick={() => onSave({ laser_model: m })} className="rounded-lg border px-3 py-2.5 text-xs font-bold" style={{ ...btn(active === m), color: active === m ? theme.accent : theme.text }}>{m}</button>)}</div>
      </>}
      <button onClick={onClose} data-testid="laser-picker-done" className="mt-4 w-full rounded-lg py-2.5 text-xs font-extrabold tracking-[2px] text-black" style={{ background: theme.accent }}>DONE</button>
    </div>
  );
};

const Chip = ({ icon, label, theme }) => (
  <span className="flex items-center rounded-full border px-[9px] py-[5px]" style={{ background: theme.cardElevated, borderColor: theme.border }}>
    <Ion name={icon} size={11} color={theme.accent} /><span className="text-[11px] font-semibold">&nbsp;{label}</span>
  </span>
);

const Welcome = ({ theme, isCustomer, active, onPickLaser, onExample, hiddenCount, onShowHistory }) => {
  const navigate = useNavigate();
  return (
    <div className="mb-4 flex flex-col items-center rounded-[14px] border p-6" style={{ background: theme.card, borderColor: theme.border }} data-testid="agent-welcome">
      <span className="mb-1 flex h-32 w-32 items-center justify-center rounded-full border" style={{ background: `${theme.accent}14`, borderColor: `${theme.accent}59` }}>
        <AgentAvatar kind="ai" size={104} ring={theme.accent} testId="agent-welcome-avatar" />
      </span>
      <p className="mt-2 text-lg font-bold">ALR Laser Cleaning Agent</p>
      <p className="mt-1.5 text-center text-[13px] leading-[18px]" style={{ color: theme.textSecondary }}>
        I'm trained on ALR's laser cleaning settings, equipment troubleshooting,{isCustomer ? "" : " the Operations Manual,"} and ANSI laser safety standards — tuned to your specific machine. Ask me anything from cleaning parameters to safety questions or machine troubleshooting.
      </p>
      {active ? (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <span className="flex items-center gap-1 rounded-full border px-2.5 py-[5px]" style={{ background: theme.cardElevated, borderColor: theme.border }} data-testid="agent-machine-badge"><Ion name="hardware-chip-outline" size={11} color={theme.accent} /><span className="text-[11px] font-semibold">{active} ALR Fiber Laser</span></span>
          <button onClick={onPickLaser} data-testid="agent-change-machine" className="flex items-center gap-[3px] rounded-full border px-2.5 py-[5px]" style={{ borderColor: theme.accent }}><Ion name="swap-horizontal-outline" size={12} color={theme.text} /><span className="text-[11px] font-bold">Change</span></button>
        </div>
      ) : (
        <div className="mt-4 flex flex-col items-center">
          <button onClick={onPickLaser} data-testid="agent-select-laser" className="flex items-center rounded-lg px-4 py-2.5" style={{ background: theme.accent }}><Ion name="hardware-chip-outline" size={14} color="#000" /><span className="text-[13px] font-bold text-black"> Select your laser model</span></button>
          <p className="mt-2 px-4 text-center text-[11px] leading-4" style={{ color: theme.textSecondary }}>Takes 5 seconds. I'll tailor every setting to your exact machine ({LASER_MODELS.join(", ")}).</p>
        </div>
      )}
      <div className="mt-6 flex w-full flex-col items-center border-t pt-4" style={{ borderColor: theme.border }}>
        <p className="mb-2 text-[10px] font-bold tracking-[1.5px]" style={{ color: theme.accent }}>I can help with</p>
        <div className="flex flex-wrap justify-center gap-1.5">
          <Chip theme={theme} icon="settings-outline" label="Cleaning settings" />
          <Chip theme={theme} icon="construct-outline" label="Troubleshooting" />
          <Chip theme={theme} icon="shield-checkmark-outline" label="Laser safety / PPE" />
          {!isCustomer && <Chip theme={theme} icon="book-outline" label="Operations Manual" />}
        </div>
      </div>
      <p className="mb-1.5 mt-4 self-start text-[10px] font-bold tracking-[1.5px]" style={{ color: theme.accent }}>EXAMPLES TO TRY</p>
      <div className="flex w-full flex-col gap-1 rounded-[14px] border border-dashed p-4" style={{ background: theme.cardElevated, borderColor: theme.border }}>
        {EXAMPLES.map((ex, i) => <button key={ex} onClick={() => onExample(ex)} data-testid={`agent-example-${i}`} className="text-left text-xs leading-[18px]" style={{ color: theme.textSecondary }}>• "{ex}"</button>)}
      </div>
      {!isCustomer && <button onClick={() => navigate("/community")} data-testid="agent-ask-community" className="mt-4 flex items-center rounded-full border px-4 py-[9px]" style={{ background: "rgba(0, 230, 118, 0.08)", borderColor: theme.accent }}><Ion name="people-outline" size={14} color={theme.accent} /><span className="text-xs font-bold" style={{ color: theme.accent }}> Or ask the Franchisee Community →</span></button>}
      {!isCustomer && hiddenCount > 0 && <button onClick={onShowHistory} data-testid="agent-show-history" className="mt-6 flex items-center rounded-full border px-4 py-2" style={{ background: theme.cardElevated, borderColor: theme.border }}><Ion name="time-outline" size={13} color={theme.accent} /><span className="text-xs font-semibold" style={{ color: theme.accent }}> Show {hiddenCount} previous message{hiddenCount === 1 ? "" : "s"}</span></button>}
    </div>
  );
};

export default function CleaningAgent() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const back = useBack();
  const theme = useTheme();
  const [profile, setProfile] = useState(user);
  const [messages, setMessages] = useState([]);
  const [sessionStart, setSessionStart] = useState(0);
  const [input, setInput] = useState("");
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [machines, setMachines] = useState([]);
  const [machineId, setMachineId] = useState(() => localStorage.getItem("alr_active_machine"));
  const isCustomer = ["machine_owner", "rental_customer"].includes(user.role);
  const canTrain = !isCustomer && user.role !== "prospect";
  const owned = profile.owned_lasers || [];
  const active = profile.laser_model || owned[0] || null;
  const activeMachine = machines.find((m) => m.id === machineId) || machines.find((m) => `${m.watts}W` === active) || null;
  const pickMachine = (m) => {
    localStorage.setItem("alr_active_machine", m.id); setMachineId(m.id);
    const w = `${m.watts}W`;
    if (["300W", "500W", "1000W", "2000W"].includes(w)) saveLasers({ owned_lasers: owned.includes(w) ? owned : [...owned, w], laser_model: w });
  };

  useEffect(() => {
    if (canTrain) api("/laser/machines").then(setMachines).catch(() => {});
  }, [canTrain]);

  useEffect(() => {
    api("/ai/history").then((items) => {
      const hist = items.map((h) => ({ role: h.role, text: h.text, sessionId: h.role === "assistant" ? h.session_id : undefined, createdAt: h.created_at }));
      setMessages(hist);
      setSessionStart(hist.length);
    }).catch(() => {});
    api("/ai/response-stats").then(setStats).catch(() => {});
  }, []);

  const saveLasers = async (body) => { try { setProfile(await api("/auth/me", { method: "PATCH", body })); refreshUser(); } catch (e) { toast.error(e.message); } };

  const send = async (override) => {
    const text = (override ?? input).trim() || (image ? "Please analyze this photo and tell me what cleaning settings would work, plus any safety concerns." : "");
    if (!text || busy) return;
    const next = [...messages, { role: "user", text, image: image || undefined, createdAt: new Date().toISOString() }];
    setMessages(next); setInput(""); setBusy(true);
    const sentImage = image;
    setImage(null);
    try {
      const res = await api("/ai/chat", { method: "POST", body: { messages: next.filter((m) => !m.isError).map((m) => ({ role: m.role, text: m.text })), language: "en", image_base64: sentImage, machine_id: activeMachine?.id || null } });
      setMessages([...next, { role: "assistant", text: res.answer, sessionId: res.session_id, createdAt: new Date().toISOString() }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", text: `**Error:** ${e.message || "AI error"}`, isError: true }]);
    } finally { setBusy(false); }
  };

  const shown = messages.slice(sessionStart);
  const rate = async (i, rating) => {
    const m = shown[i];
    try {
      await api("/ai/feedback", { method: "POST", body: { session_id: m.sessionId, question: shown[i - 1]?.text || "", answer: m.text, rating } });
      markRated(m.sessionId);
      setMessages((prev) => prev.map((x) => (x === m ? { ...x, feedbackSent: true } : x)));
    } catch (e) { toast.error(e.message); }
  };
  const clear = async () => {
    if (!window.confirm("Clear your entire chat history with the Cleaning Agent?")) return;
    try { await api("/ai/history", { method: "DELETE" }); setMessages([]); setSessionStart(0); } catch (e) { toast.error(e.message); }
  };
  const box = "flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border";
  const labeled = "flex min-h-9 items-center gap-[5px] rounded-[10px] border px-2.5 py-2";

  return (
    <div className="flex min-h-screen flex-col" style={{ background: theme.bg, color: theme.text }} data-testid="cleaning-agent-page">
      <div className="flex items-center gap-2 p-4">
        <button data-testid="back-btn" onClick={back} className={box} style={{ background: theme.card, borderColor: theme.border }}><Ion name="chevron-back" size={22} color={theme.text} /></button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[11px] font-bold tracking-[3px]" style={{ color: theme.accent }}>AI</p>
          <h1 className="mt-0.5 truncate text-lg font-extrabold" data-testid="page-title">ALR Cleaning Agent</h1>
        </div>
        {messages.length > 0 && <button onClick={clear} data-testid="chat-clear-btn" aria-label="Clear history" className={box} style={{ background: theme.card, borderColor: theme.border }}><Ion name="trash-outline" size={18} color={theme.textSecondary} /></button>}
        {canTrain && <button onClick={() => navigate("/my-presets")} data-testid="ai-my-presets-btn" className={labeled} style={{ background: theme.card, borderColor: theme.accent }}><Ion name="bulb-outline" size={16} color={theme.accent} /><span className="text-xs font-extrabold tracking-[0.3px]" style={{ color: theme.accent }}>My Settings</span></button>}
        {canTrain && <button onClick={() => setShowAdd(true)} data-testid="ai-add-setting-btn" className={labeled} style={{ background: `${theme.accent}14`, borderColor: theme.accent }}><Ion name="add" size={16} color={theme.accent} /><span className="text-xs font-extrabold tracking-[0.3px]" style={{ color: theme.accent }}>Add Setting</span></button>}
        <button onClick={() => setShowPicker((v) => !v)} data-testid="ai-laser-chip" className="flex max-w-[160px] items-center gap-1.5 rounded-full border px-2.5 py-2" style={{ borderColor: theme.accent, background: `${theme.accent}14` }}>
          <Ion name="hardware-chip-outline" size={14} color={theme.accent} /><span className="truncate text-[11px] font-bold tracking-[1px]" style={{ color: theme.accent }}>{activeMachine ? (activeMachine.nickname || activeMachine.name) : active || "Set laser"}</span><Ion name="chevron-down" size={14} color={theme.accent} />
        </button>
      </div>
      <AddSettingDialog open={showAdd} onOpenChange={setShowAdd} defaultModel={active} />
      {showPicker && <LaserPicker theme={theme} owned={owned} active={active} onSave={saveLasers} onClose={() => setShowPicker(false)} machines={machines} machineId={activeMachine?.id} onMachine={pickMachine} />}
      <div className="flex-1 p-6 pb-4">
        {canTrain && <div className="mb-4"><TeamTestsNotice /></div>}
        <ChatThread messages={shown} busy={busy} onRate={rate} stats={stats} empty={
          <Welcome theme={theme} isCustomer={isCustomer} active={active} onPickLaser={() => setShowPicker(true)} onExample={(ex) => send(ex)} hiddenCount={sessionStart} onShowHistory={() => setSessionStart(0)} />
        } />
      </div>
      <ChatComposer value={input} onChange={setInput} onSend={() => send()} busy={busy} placeholder="Ask about settings, troubleshooting, or attach a photo…" image={image} onImage={setImage} onClearImage={() => setImage(null)} />
    </div>
  );
}
